import Card from "@material-tailwind/react/Card";
import CardBody from "@material-tailwind/react/CardBody";
import React, { useEffect, useMemo, useState } from "react";
import { BiEdit, BiSearch } from "react-icons/bi";
import { BsFileEarmarkExcelFill, BsFileEarmarkPdfFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { downloadExcel } from "react-export-table-to-excel";

import { Colors } from "../../assets/Colors";
import Spinner from "../spinner/Spinner";
import Skeltons from "../loading/Skeltons";

import { maxPageGlobal } from "../../assets/config";
import {
  getMinTipeKaryawan,
  tipeKaryawanSelector,
} from "../../features/master/tipe-karyawan/tipeKaryawanSlice";

const EXPORT_HEADERS = ["No", "Nik", "PIN", "Nama Karyawan", "Bagian", "Bisnis Unit"];

export default function TableListKaryawanPayroll(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tipeKaryawan = useSelector(tipeKaryawanSelector);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTipeKaryawanValue, setsearchTipeKaryawanValue] = useState("");
  const perPage = maxPageGlobal;

  // Load opsi tipe karyawan saat mount
  useEffect(() => {
    dispatch(getMinTipeKaryawan({ search: "" }));
  }, [dispatch]);

  // Data mentah dari mutation/props
  let rawData = props.tableData;
  if (props.usingMutation && !props.mutation.isLoading && rawData) {
    rawData = rawData.data;
  }
  const listData = Array.isArray(rawData) ? rawData : [];

  // Search teks client-side (Nik / PIN / Nama / Bagian / Bisnis Unit)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return listData;
    const keys = ["Nik", "PIN", "NamaKaryawan", "NamaBagian", "NamaBisnisUnit"];
    return listData.filter((r) =>
      keys.some((k) => (r[k] || "").toString().toLowerCase().includes(q))
    );
  }, [listData, search]);

  // Reset ke halaman 1 tiap kali filter/search berubah
  useEffect(() => {
    setCurrentPage(1);
  }, [search, searchTipeKaryawanValue]);

  const totalPage = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(currentPage, totalPage);
  const paged = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  // Nomor halaman yang ditampilkan (maks 5, mengikuti halaman aktif)
  const pageNumbers = useMemo(() => {
    const nums = [];
    let start = Math.max(1, safePage - 2);
    let end = Math.min(totalPage, start + 4);
    start = Math.max(1, end - 4);
    for (let p = start; p <= end; p++) nums.push(p);
    return nums;
  }, [safePage, totalPage]);

  const buildRows = () =>
    filtered.map((r, i) => [
      i + 1,
      r["Nik"] || "",
      r["PIN"] || "",
      r["NamaKaryawan"] || "",
      r["NamaBagian"] || "",
      r["NamaBisnisUnit"] || "",
    ]);

  const handleExportExcel = () => {
    if (!filtered.length) {
      toast.warn("Tidak ada data untuk di-export", { position: "top-center" });
      return;
    }
    downloadExcel({
      fileName: "Daftar_Karyawan_Payroll",
      sheet: "Karyawan",
      tablePayload: { header: EXPORT_HEADERS, body: buildRows() },
    });
  };

  const handleExportPDF = () => {
    if (!filtered.length) {
      toast.warn("Tidak ada data untuk di-export", { position: "top-center" });
      return;
    }
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Daftar Karyawan Payroll", 14, 15);
    autoTable(doc, {
      startY: 22,
      head: [EXPORT_HEADERS],
      body: buildRows(),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
    });
    doc.save("Daftar_Karyawan_Payroll.pdf");
  };

  const thClass =
    "font-bold px-4 align-middle border-b border-solid border-gray-200 text-gray-800 py-3 text-base whitespace-nowrap text-left";
  const tdClass =
    "border-b border-gray-200 align-middle font-light text-sm whitespace-nowrap px-2 py-1 text-left";
  const pageBtn =
    "py-2 px-3 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 cursor-pointer";

  return (
    <Card className="flex flex-col items-center content-center">
      <>
        {props.fetching ? (
          <div className="flex items-center">
            <Spinner />
          </div>
        ) : (
          <CardBody className="w-full pt-0 ">
            {/* Toolbar: filter tipe karyawan + search + export */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-3">
              <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                <div className="flex flex-col">
                  <label className="text-xs font-bold mb-1" style={{ color: Colors.primary }}>
                    Tipe Karyawan
                  </label>
                  <select
                    value={searchTipeKaryawanValue}
                    onChange={(e) => {
                      setsearchTipeKaryawanValue(e.target.value);
                      props.searchTipeKaryawanHandler(e.target.value);
                    }}
                    name="TipeKaryawanID"
                    className="appearance-none w-full md:w-56 text-gray-700 border border-gray-300 rounded py-2 px-3 leading-tight outline-none bg-white"
                  >
                    <option value="">-- Semua Tipe --</option>
                    {tipeKaryawan.minData.map((item) => (
                      <option key={item.TipeKaryawanID} value={item.NamaTipeKaryawan}>
                        {item.NamaTipeKaryawan}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-bold mb-1" style={{ color: Colors.primary }}>
                    Cari
                  </label>
                  <div className="relative">
                    <BiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Nik / PIN / Nama / Bagian..."
                      className="w-full md:w-72 text-gray-700 border border-gray-300 rounded py-2 pl-8 pr-3 leading-tight outline-none bg-white"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-row gap-2">
                <button
                  type="button"
                  onClick={handleExportExcel}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2 px-4 rounded shadow"
                >
                  <BsFileEarmarkExcelFill /> Excel
                </button>
                <button
                  type="button"
                  onClick={handleExportPDF}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2 px-4 rounded shadow"
                >
                  <BsFileEarmarkPdfFill /> PDF
                </button>
              </div>
            </div>

            <div className="overflow-x-auto relative">
              <table className="table-auto w-full bg-transparent border-collapse text-sm">
                <thead>
                  <tr>
                    <th style={{ color: Colors.primary }} className={thClass}>Nik</th>
                    <th style={{ color: Colors.primary }} className={thClass}>PIN</th>
                    <th style={{ color: Colors.primary }} className={thClass}>Nama Karyawan</th>
                    <th style={{ color: Colors.primary }} className={thClass}>Bagian</th>
                    <th style={{ color: Colors.primary }} className={thClass}>Bisnis Unit</th>
                    <th style={{ color: Colors.primary }} className={thClass}>#</th>
                  </tr>
                </thead>
                <tbody>
                  {props.usingMutation && props.mutation.isLoading ? (
                    <Skeltons />
                  ) : paged.length ? (
                    paged.map((keyName, i) => (
                      <tr key={(keyName["PIN"] || keyName["Nik"] || "row") + i}>
                        <th className={tdClass}>{keyName["Nik"]}</th>
                        <th className={tdClass}>{keyName["PIN"]}</th>
                        <th className={tdClass}>{keyName["NamaKaryawan"]}</th>
                        <th className={tdClass}>{keyName["NamaBagian"]}</th>
                        <th className={tdClass}>{keyName["NamaBisnisUnit"]}</th>
                        <th className={tdClass}>
                          <button
                            onClick={() => navigate("/payroll/detail/" + keyName["PIN"])}
                            className="flex flex-row items-center bg-white hover:bg-gray-100 text-gray-800 font-semibold py-1 px-4 border border-gray-400 rounded shadow"
                          >
                            <BiEdit className="text-xl text-gray-800 mr-2" />
                            <span>Detail</span>
                          </button>
                        </th>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6}>
                        <div className="w-full flex flex-col justify-center items-center py-6">
                          <BsFileEarmarkExcelFill className="text-7xl py-2 text-gray-400" />
                          <div className="pb-2 text-gray-800">Tidak ada data</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Info + pagination */}
            <div className="flex flex-col md:flex-row items-center justify-between mt-3 gap-2">
              <div className="text-xs text-gray-500">
                {filtered.length > 0
                  ? `Menampilkan ${(safePage - 1) * perPage + 1}-${Math.min(
                      safePage * perPage,
                      filtered.length
                    )} dari ${filtered.length} karyawan`
                  : "0 karyawan"}
              </div>
              <nav aria-label="Pagination">
                <ul className="flex">
                  <li>
                    <span
                      onClick={() => currentPage > 1 && setCurrentPage(safePage - 1)}
                      className={pageBtn + " ml-0 rounded-l-lg"}
                    >
                      Previous
                    </span>
                  </li>
                  {pageNumbers.map((p) => (
                    <li key={p}>
                      <span
                        onClick={() => setCurrentPage(p)}
                        className={
                          pageBtn +
                          (p === safePage
                            ? " text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700"
                            : "")
                        }
                      >
                        {p}
                      </span>
                    </li>
                  ))}
                  <li>
                    <span
                      onClick={() => safePage < totalPage && setCurrentPage(safePage + 1)}
                      className={pageBtn + " rounded-r-lg"}
                    >
                      Next
                    </span>
                  </li>
                </ul>
              </nav>
            </div>
          </CardBody>
        )}
      </>
    </Card>
  );
}
